// Turns the flat folder list the share endpoint returns into the rows the
// picker draws, and works out which ones are coming along whether or not
// they were ticked.
//
// Kept out of the dialog because "what is actually going out" is the part
// that has to be right — a folder wrongly shown as unticked while its
// parent is ticked is a folder the sender did not know they sent.

// Folder ids arrive as strings from both the share endpoint and the
// context menu, but the two travel by different routes and a mismatch
// here fails silently — nothing pre-ticked, or a folder that cannot be
// ticked at all — which is the wrong kind of failure for a screen that
// decides what leaves the firm. Coerced once, on the way in.
const asId = (value) => (value === null || value === undefined ? null : String(value));

/** Depth-first rows with a depth for indenting, parents before children. */
export const buildFolderRows = (raw = []) => {
  const folders = (raw || []).map((folder) => ({
    ...folder,
    id: asId(folder.id),
    parent_id: asId(folder.parent_id),
  }));
  const byParent = new Map();
  const known = new Set(folders.map((folder) => folder.id));

  folders.forEach((folder) => {
    // A folder whose parent is outside this file, missing, or itself is
    // hung at the top rather than dropped.
    const parent =
      folder.parent_id !== folder.id && known.has(folder.parent_id)
        ? folder.parent_id
        : null;
    if (!byParent.has(parent)) byParent.set(parent, []);
    byParent.get(parent).push(folder);
  });

  const rows = [];
  const seen = new Set();

  const walk = (parentId, depth) => {
    (byParent.get(parentId) || []).forEach((folder) => {
      if (seen.has(folder.id)) return;
      seen.add(folder.id);
      rows.push({ ...folder, depth });
      walk(folder.id, depth + 1);
    });
  };

  walk(null, 0);

  // Anything the walk could not reach — folders whose parent chain loops
  // among themselves — is shown at the top. A folder missing from this
  // list is one nobody can share, which is worse than one drawn flat.
  folders.forEach((folder) => {
    if (!seen.has(folder.id)) {
      seen.add(folder.id);
      rows.push({ ...folder, depth: 0, parent_id: null });
    }
  });

  return rows;
};

const walkUpToSelected = (parentOf, selectedIds, id) => {
  let ancestor = parentOf.get(asId(id));
  const walked = new Set();
  while (ancestor && !walked.has(ancestor)) {
    walked.add(ancestor);
    if (selectedIds.has(ancestor)) return true;
    ancestor = parentOf.get(ancestor);
  }
  return false;
};

/**
 * Ids going out because an ancestor is ticked, though nobody ticked them.
 *
 * A shared folder always carries its subfolders, so the dialog has to show
 * these as included or it understates what is being sent.
 */
export const impliedByAncestor = (rows, rawSelected) => {
  const parentOf = new Map(rows.map((row) => [row.id, row.parent_id]));
  const selectedIds = new Set([...rawSelected].map(asId));
  return new Set(
    rows
      .filter(
        (row) =>
          !selectedIds.has(row.id) &&
          walkUpToSelected(parentOf, selectedIds, row.id)
      )
      .map((row) => row.id)
  );
};

/**
 * The folders to put on the link.
 *
 * A ticked folder inside another ticked folder is dropped: it is already
 * covered, and sending both would list and zip its documents twice.
 */
export const foldersToSend = (rows, rawSelected) => {
  const parentOf = new Map(rows.map((row) => [row.id, row.parent_id]));
  const selectedIds = new Set([...rawSelected].map(asId));
  return [...selectedIds].filter(
    (id) => !walkUpToSelected(parentOf, selectedIds, id)
  );
};
